/**
 * Klaviyo restock popup — Concept theme integration.
 *
 * Defines <restock-popup-drawer> as an extension of Concept's native
 * DrawerElement so it inherits the theme's opening/closing transitions,
 * overlay, gesture, focus-trap and body-scroll-lock behaviors. The form
 * is submitted via AJAX to Klaviyo's public client API.
 */
(function () {
  'use strict';

  const KLAVIYO_REVISION = '2025-07-15';
  const KLAVIYO_MARKETING_SOURCE = 'Back in Stock';
  const SELECTOR_TRIGGER = '.restock-popup-trigger';
  const SELECTOR_OPTION_TRIGGER = '[data-restock-trigger]';
  const SELECTOR_POPUP = 'restock-popup-drawer';

  /**
   * Une ligne de libellé → align-items center (case et texte centrés entre eux).
   * Plusieurs lignes → flex-start (case en haut, comme avant).
   */
  function resolveLabelLineHeightPx(label) {
    const style = getComputedStyle(label);
    const lh = style.lineHeight;
    if (lh === 'normal') {
      const fs = parseFloat(style.fontSize);
      return Number.isFinite(fs) ? fs * 1.25 : 0;
    }
    const parsed = parseFloat(lh);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function syncRestockMarketingRowLayout(popup) {
    const row = popup.querySelector('[data-restock-marketing-row]');
    const label = popup.querySelector('[data-restock-marketing-label]');
    if (!row || !label) return;

    if (!popup.hasAttribute('open')) {
      row.classList.remove('restock-popup__marketing--single-line');
      return;
    }

    const lineHeightPx = resolveLabelLineHeightPx(label);
    if (lineHeightPx <= 0) return;

    const tolerance = 8;
    const singleLine = label.scrollHeight <= lineHeightPx + tolerance;
    row.classList.toggle('restock-popup__marketing--single-line', singleLine);
  }

  function setupRestockMarketingRowLayout(popup) {
    const row = popup.querySelector('[data-restock-marketing-row]');
    if (!row || row.dataset.restockMarketingLayoutBound === 'true') return;

    const label = row.querySelector('[data-restock-marketing-label]');
    if (!label) return;

    row.dataset.restockMarketingLayoutBound = 'true';

    const run = () => {
      requestAnimationFrame(() => syncRestockMarketingRowLayout(popup));
    };

    popup.addEventListener('drawer:afterShow', run);

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(run).observe(label);
    }

    window.addEventListener('resize', run, { passive: true });

    if (document.fonts && typeof document.fonts.ready !== 'undefined') {
      document.fonts.ready.then(run);
    }
  }

  function getStrings() {
    return (window.theme && window.theme.restockStrings) || {};
  }

  function getPopupById(sectionId, productId) {
    return document.getElementById(`RestockPopup-${sectionId}-${productId}`);
  }

  function getPopupForTrigger(trigger) {
    const productInfo = trigger.closest('product-info');
    if (!productInfo) return null;

    const productId = productInfo.getAttribute('data-product-id');
    const sectionId = productInfo.getAttribute('data-section-id');
    return getPopupById(sectionId, productId);
  }

  function getVariantsData(popup) {
    const node = popup.querySelector('[data-restock-variants]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return null;
    }
  }

  function resolveVariantIdFromTrigger(trigger, popup) {
    if (trigger.hasAttribute('data-restock-variant-id')) {
      return trigger.getAttribute('data-restock-variant-id');
    }

    const productInfo = trigger.closest('product-info');
    const productForm = productInfo ? document.forms[productInfo.getAttribute('form')] : null;
    const input = productForm ? productForm.querySelector('input[name="id"]') : null;
    if (input && input.value) return input.value;

    const data = getVariantsData(popup);
    if (!data) return '';

    const unavailable = data.variants.find((variant) => !variant.available);
    return unavailable ? String(unavailable.id) : '';
  }

  function setVariantSelection(popup, variantId) {
    if (!variantId) return;
    const select = popup.querySelector('[data-restock-variant-select]');
    if (!select) return;

    if (select.tagName === 'SELECT') {
      const option = Array.from(select.options).find((opt) => String(opt.value) === String(variantId));
      if (option) select.value = option.value;
    } else {
      select.value = String(variantId);
    }
  }

  function renderAlert(popup, status, message) {
    const host = popup.querySelector('[data-restock-alert]');
    if (!host) return;

    if (!message) {
      host.hidden = true;
      host.innerHTML = '';
      return;
    }

    host.hidden = false;
    host.innerHTML = `<div class="alert alert--${status} flex items-start gap-3 text-sm md:text-base leading-tight">${message}</div>`;
  }

  /**
   * Delegates the visual loading state to the native `hover-button` custom
   * element. HoverButton observes `aria-busy` and swaps the label for its
   * animated loader spans. We simply toggle the attribute and `disabled`.
   */
  function setLoading(popup, isLoading) {
    const button = popup.querySelector('[data-restock-submit]');
    if (!button) return;

    button.disabled = isLoading;
    if (isLoading) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  function resetForm(popup) {
    renderAlert(popup, null, '');
    const form = popup.querySelector('form');
    if (form) form.reset();
  }

  function openPopup(popup, trigger) {
    if (!popup) return;
    resetForm(popup);

    const variantId = resolveVariantIdFromTrigger(trigger, popup);
    setVariantSelection(popup, variantId);

    if (typeof popup.show === 'function') {
      popup.show(trigger);
    } else {
      popup.setAttribute('open', '');
    }
  }

  async function submitBackInStock(publicKey, email, variantId) {
    const body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` }
          }
        }
      }
    };

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'revision': KLAVIYO_REVISION
        },
        body: JSON.stringify(body)
      }
    );

    if (!response.ok && response.status !== 202) {
      const errorBody = await response.json().catch(() => ({}));
      const error = (errorBody.errors && errorBody.errors[0]) || {};
      const code = error.code || '';
      throw new Error(code === 'duplicate_profile' ? 'duplicate' : (error.detail || 'error'));
    }
  }

  async function submitMarketingSubscription(publicKey, listId, email) {
    if (!listId) return;

    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: KLAVIYO_MARKETING_SOURCE,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'revision': KLAVIYO_REVISION
        },
        body: JSON.stringify(body)
      }
    ).catch(() => null);
  }

  async function onFormSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const popup = form.closest(SELECTOR_POPUP);
    if (!popup) return;

    // HoverButton auto-toggles aria-busy on form submit. The top-level
    // finally guarantees we reset it on every exit path (validation
    // errors, API success, API failure, or unexpected exceptions).
    setLoading(popup, true);

    try {
      const strings = getStrings();
      const publicKey = popup.getAttribute('data-public-key');
      const listId = popup.getAttribute('data-list-id');

      if (!publicKey) {
        renderAlert(popup, 'error', strings.errorDefault || 'Configuration error.');
        return;
      }

      const emailInput = form.querySelector('input[type="email"]');
      const variantInput = form.querySelector('[data-restock-variant-select]');
      const marketingInput = form.querySelector('[data-restock-marketing]');

      const email = emailInput ? emailInput.value.trim() : '';
      const variantId = variantInput ? variantInput.value.trim() : '';
      const wantsMarketing = marketingInput ? marketingInput.checked : false;

      if (!email || !variantId) {
        renderAlert(popup, 'error', strings.errorDefault || 'Please complete the form.');
        return;
      }

      renderAlert(popup, null, '');

      try {
        await submitBackInStock(publicKey, email, variantId);
        if (wantsMarketing) {
          await submitMarketingSubscription(publicKey, listId, email);
        }
        renderAlert(popup, 'success', strings.success || 'You are subscribed.');
        form.reset();
      } catch (error) {
        if (error.message === 'duplicate') {
          renderAlert(popup, 'info', strings.duplicate || 'You are already subscribed.');
        } else {
          renderAlert(popup, 'error', strings.errorDefault || 'Something went wrong.');
        }
      }
    } finally {
      setLoading(popup, false);
    }
  }

  function bindForm(popup) {
    if (popup.dataset.restockBound === 'true') return;
    const form = popup.querySelector('form');
    if (!form) return;
    popup.dataset.restockBound = 'true';
    form.addEventListener('submit', onFormSubmit);
  }

  function handleTriggerClick(event) {
    const trigger = event.target.closest(SELECTOR_TRIGGER);
    if (!trigger) return;

    const productInfo = trigger.closest('product-info');
    if (!productInfo) return;

    const productForm = document.forms[productInfo.getAttribute('form')];
    const variantAvailable = productForm?.querySelector('input[name="id"]')?.disabled === false;
    const isOptionTrigger = trigger.matches(SELECTOR_OPTION_TRIGGER);

    // Intercept only when an option trigger targets a sold-out option,
    // or when the buy submit is clicked for an unavailable variant.
    if (!isOptionTrigger && variantAvailable) return;

    event.preventDefault();
    event.stopPropagation();

    const popup = getPopupForTrigger(trigger);
    if (!popup) return;

    bindForm(popup);
    openPopup(popup, trigger);
  }

  function handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant || variant.available) return;

    const productInfo = event.target.closest ? event.target.closest('product-info') : null;
    if (!productInfo) return;

    const productId = productInfo.getAttribute('data-product-id');
    const sectionId = productInfo.getAttribute('data-section-id');
    const popup = getPopupById(sectionId, productId);
    if (!popup) return;

    setVariantSelection(popup, variant.id);
  }

  function registerCustomElement() {
    if (customElements.get(SELECTOR_POPUP)) return;

    const DrawerElement = customElements.get('drawer-element');
    if (!DrawerElement) return;

    class RestockPopupDrawer extends DrawerElement {
      connectedCallback() {
        super.connectedCallback();
        bindForm(this);
        setupRestockMarketingRowLayout(this);
      }
    }

    customElements.define(SELECTOR_POPUP, RestockPopupDrawer);
  }

  function init() {
    document.addEventListener('click', handleTriggerClick, true);
    document.addEventListener('variant:change', handleVariantChange, true);

    if (customElements.get('drawer-element')) {
      registerCustomElement();
    } else {
      customElements.whenDefined('drawer-element').then(registerCustomElement);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
